import React, { FunctionComponent, useEffect } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import { Link, Text } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import {
  CheckoutState,
  setConfirmTermsViewed,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCreditScoreDisclosureDocumentId,
  useIsConsumerLendingAutopayEnabled,
  useIsSupOrder,
  useLoanAgreementDocumentId,
  usePaymentAuthorisationDocumentId,
} from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import styles from './style.module.css'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model'
import { useMoney } from '../Money/useMoney'

interface TermsProps {
  setDocumentIdToShow: (documentId: string, isTilDisclosure?: boolean) => void
}

export const Terms: FunctionComponent<TermsProps> = ({ setDocumentIdToShow }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const { extraSmallTextSize } = useCheckoutSizes()
  const state: CheckoutState = useCheckoutState()
  const autopayEnabled = useIsConsumerLendingAutopayEnabled()
  const paymentAuthorisationDocumentId = usePaymentAuthorisationDocumentId()
  const loanAgreementDocumentId = useLoanAgreementDocumentId()
  const creditScoreDisclosureDocumentId = useCreditScoreDisclosureDocumentId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()

  const isSupPayMonthlyEnabled = useFlag(Features.PAY_MONTHLY_FOR_SUP_ENABLED)
  const isSupPMOrder = useIsSupOrder() && isSupPayMonthlyEnabled
  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount

  const { formatMoney } = useMoney({
    locale,
    currency: orderAmount?.currency,
  })

  const components = {
    autoPayAuthorization: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='auto-payment-authorisation-link'
        key='til-autopay-authorisation'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_PAYMENT_AUTHORISATION_LINK)
          setDocumentIdToShow(paymentAuthorisationDocumentId)
        }}
      />
    ),
    truthInLendingDisclosure: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='payment-authorisation-link'
        key='til-disclosure'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK)
          setDocumentIdToShow(loanAgreementDocumentId, true)
        }}
      />
    ),
    loanAgreement: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='loan-agreement-link'
        key='til-loan-agreement'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_LOAN_AGREEMENT_LINK)
          setDocumentIdToShow(loanAgreementDocumentId)
        }}
      />
    ),
    creditScoreDisclosure: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='credit-score-disclosure-link'
        key='til-credit-score-disclosure'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_CREDIT_SCORE_DISCLOSURE_LINK)
          setDocumentIdToShow(creditScoreDisclosureDocumentId)
        }}
      />
    ),
    cardAccountAgreement: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='card-account-agreement-link'
        key='til-card-account-agreement'
        target='_blank'
        href={t('consumerLending:til:cardAccountAgreementUrl')}
      />
    ),
    br: <br />,
  }

  const getTransContext = (): string => {
    if (isSupPMOrder) {
      if (autopayEnabled) {
        return 'supAutopay'
      }
      return 'sup'
    }

    return autopayEnabled ? 'noTilAutopay' : 'noTil'
  }

  const i18nKey = 'consumerLending:til:terms'

  useEffect(() => {
    const termsKey = `${i18nKey}_${getTransContext()}`
    dispatch(setConfirmTermsViewed(termsKey))
  }, [i18nKey, isSupPMOrder, autopayEnabled])

  const context = getTransContext()

  return (
    <div className={styles.termsContainer}>
      <Text color='Gray40' size={extraSmallTextSize}>
        <Trans
          key={context}
          i18nKey={i18nKey}
          components={components}
          context={context}
          values={{
            supAmount: formatMoney(orderAmount?.amount),
          }}
        />
      </Text>
    </div>
  )
}
